import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getVillages, addBeneficiary, updateBeneficiary } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';

const BeneficiaryAdd = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    village_id: '',
    name: '',
    gender: '',
    date_of_birth: '',
    contact_number: '',
    category: '',
    aadhar_number: '',
    household_id: '',
    active_status: true,
  });

  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchVillages() {
      try {
        const asha_id = localStorage.getItem('asha_id');
        const response = await getVillages(asha_id);
        if (response.status === true) {
          setVillages(response.data);
        } else {
          showError(response.message || 'Failed to fetch villages');
        }
      } catch (err) {
        showError('Network error while fetching villages');
      }
    }

    // Fetch beneficiarie

    fetchVillages();
  }, [id, isEditMode]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Validate and save form
  const handleSave = async () => {
    // Basic validation
    if (!formData.name || !formData.village_id || !formData.gender || !formData.date_of_birth || !formData.category || !formData.household_id) {
      showError('Please fill all required fields');
      return;
    }

    // Aadhar number validation (12 digits, optional)
    if (formData.aadhar_number && !/^\d{12}$/.test(formData.aadhar_number)) {
      showError('Aadhar number must be 12 digits');
      return;
    }

    // Contact number validation (10 digits, optional)
    if (formData.contact_number && !/^\d{10}$/.test(formData.contact_number)) {
      showError('Contact number must be 10 digits');
      return;
    }

    const payload = {
      ...(isEditMode && { beneficiary_id: id }), // Include beneficiary_id only in edit mode
      village_id: parseInt(formData.village_id),
      name: formData.name,
      gender: formData.gender,
      date_of_birth: formData.date_of_birth,
      contact_number: formData.contact_number || null,
      category: formData.category,
      aadhar_number: formData.aadhar_number || null,
      household_id: formData.household_id,
      active_status: formData.active_status,
    };

    try {
      setLoading(true);
      let response;
      if (isEditMode) {
        response = await updateBeneficiary(id, payload);
      } else {
        response = await addBeneficiary(payload);
      }

      if (response.status === true) {
        showSuccess(response.message || `Beneficiary ${isEditMode ? 'updated' : 'added'} successfully`);
        navigate('/beneficiaries-view');
      } else {
        showError(response.message || 'Failed to save beneficiary');
      }
    } catch (err) {
      showError('Network error or something went wrong');
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <>
      <style>
        {`
          .section-title {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 10px;
          }
          input[type="text"],
          input[type="date"],
          select {
            width: 100%;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 16px;
          }
          .radio-group {
            display: flex;
            gap: 20px;
          }
          .radio-option {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .checkbox-option {
            display: flex;
            align-items: center;
            gap: 5px;
          }
        `}
      </style>
      <div className="page-header">
      
        <button className="back-button" onClick={() => navigate('/beneficiaries-view')}>
          <i className="fas fa-arrow-left"></i>
          Back
        </button>
        <div className="page-title">{isEditMode ? 'Edit Beneficiary' : 'New Beneficiary'}</div>
      </div>

      <div className="form-card">
       




        {/* Name */}
        <div className="form-section">
          <div className="section-title">Name</div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter name"
            required
          />
        </div>

        <hr className="divider" />

                {/* Village Selection */}
                <div className="form-section">
          <div className="section-title">Village</div>
          <select name="village_id" value={formData.village_id} onChange={handleChange} required>
            <option value="" disabled>Select village...</option>
            {villages.map((village) => (
              <option key={village.village_id} value={village.village_id}>
                {village.village_name}
              </option>
            ))}
          </select>
        </div>


        <hr className="divider" />
        {/* Gender */}
        <div className="form-section">
          <div className="section-title">Gender</div>
          <div className="radio-group">
            {['Male', 'Female', 'Other'].map((gender) => (
              <div className="radio-option" key={gender}>
                <input
                  type="radio"
                  id={gender}
                  name="gender"
                  value={gender}
                  checked={formData.gender === gender}
                  onChange={handleChange}
                  required
                />
                <label htmlFor={gender}>{gender}</label>
              </div>
            ))}
          </div>
        </div>

        <hr className="divider" />

        {/* Date of Birth */}
        <div className="form-section">
          <div className="section-title">Date of Birth</div>
          <input
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            required
          />
        </div>

        <hr className="divider" />

        {/* Contact Number */}
        <div className="form-section">
          <div className="section-title">Contact Number (Optional)</div>
          <input
            type="text"
            name="contact_number"
            value={formData.contact_number}
            onChange={handleChange}
            placeholder="Enter 10-digit contact number"
          />
        </div>

        <hr className="divider" />

        {/* Category */}
        <div className="form-section">
          <div className="section-title">Category</div>
          <div className="radio-group">
            {['Pregnant Woman', 'Child', 'Elderly', 'Other'].map((category) => (
              <div className="radio-option" key={category}>
                <input
                  type="radio"
                  id={category}
                  name="category"
                  value={category}
                  checked={formData.category === category}
                  onChange={handleChange}
                  required
                />
                <label htmlFor={category}>{category}</label>
              </div>
            ))}
          </div>
        </div>

        <hr className="divider" />

        {/* Aadhar Number */}
        <div className="form-section">
          <div className="section-title">Aadhar Number (Optional)</div>
          <input
            type="text"
            name="aadhar_number"
            value={formData.aadhar_number}
            onChange={handleChange}
            placeholder="Enter 12-digit Aadhar number"
          />
        </div>

        <hr className="divider" />

        {/* Household ID */}
        <div className="form-section">
          <div className="section-title">Household ID</div>
          <input
            type="text"
            name="household_id"
            value={formData.household_id}
            onChange={handleChange}
            placeholder="Enter household ID"
            required
          />
        </div>

        <hr className="divider" />

        {/* Active Status */}
        <div className="form-section">
          <div className="section-title">Active Status</div>
          <div className="checkbox-option">
            <input
              type="checkbox"
              id="active_status"
              name="active_status"
              checked={formData.active_status}
              onChange={handleChange}
            />
            <label htmlFor="active_status">Active</label>
          </div>
        </div>

        <hr className="divider" />

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="cancel-button" onClick={() => navigate('/beneficiaries')} disabled={loading}>
            Cancel
          </button>
          <button className="save-button" onClick={handleSave} disabled={loading}>
            <i className="fas fa-save"></i> {isEditMode ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default BeneficiaryAdd;